import { applicationContext, uuid, QueryUtility, toUTCDate, getErrorMessage, resolveInitialEligibilityStatus } from 'core/utils';
import { BaseViewStore, BaasicDropdownStore } from 'core/stores';
import NikoDxCodeDropdownStore from 'application/niko-patient/stores/NikoDxCodeDropdownStore';
import { action, observable, computed, runInAction, set } from 'mobx';
import { FacilityOrderService, FacilityOrderStatusService, ItemSubcategoryService } from "application/facility-order/services";
import { FacilityService } from "application/facility/services";
import { LookupService } from "common/services";
import { ItemCategoryService } from 'application/product/services';
import _ from 'lodash';
import { NikoPatientDetailService, NikoPatientTemplateService, NikoPatientTemplateItemService } from "application/niko-patient/services";
import { ModalParams } from 'core/models';
import { FacilityInventoryCountDetailService } from "application/facility-inventory/services";
import { DocumentationRequestService } from "application/documentation-request/services";
import { HistoryService } from 'application/history/services';
import { DiagnosisCodeService, LocalDoctorService } from "application/import/services";
import { MissingDocumentService } from 'application/missing-document/services';
import { ClinicalStatusService } from 'application/clinical-status/services';
import { EligibilityStatusService } from 'application/eligibility-status/services';
import moment from 'moment';

@applicationContext
class NikoFacilityOrderClinicalReviewPreviewViewStore extends BaseViewStore {
    @observable accessor facilityOrder;
    @observable accessor refreshKey = uuid();
    @observable accessor selectedStatusAbrv;
    @observable accessor selectedQuestions = [];
    @observable accessor itemCategories = [];
    @observable accessor fetchingData = false;
    @observable accessor disabled = false;
    @observable accessor nikoPatientDetails = [];
    @observable accessor importingToNikoTemplateIds = [];
    @observable accessor creatingNikoSOs = false;
    @observable accessor insuranceChangeHistoryMap = {};
    @observable accessor showInsuranceChangeHistory = null;

    @computed
    get clinicalReviewStatus() { return this.facilityOrder ? this.facilityOrder.facilityOrderStatuses.find(x => x.status.abrv == "clinical-review") : undefined; }
    @computed
    get usIntakeStatus() { return this.facilityOrder ? this.facilityOrder.facilityOrderStatuses.find(x => x.status.abrv == "us-intake") : undefined; }
    @computed
    get disabledByStatus() { return !(this.clinicalReviewStatus && this.clinicalReviewStatus.startedOn); }
    @computed
    get disabledBySendOrder() { return !this.isAdmin && this.facilityOrder && this.facilityOrder.sendOrder; }

    @observable accessor isReviewed = undefined;
    @observable accessor isEmpty = false;
    @observable accessor isDocumentationRequestSent = false;
    @observable accessor hasMissingDocuments = false;
    @observable accessor isInventoryRequestSent = false;
    @observable accessor isSyncInProgress = false;
    @observable accessor notification;
    @observable accessor templateExpanded = { id: null, expanded: false };

    tabItems = [{ abrv: 'in-progress', name: 'In Progress' }, { abrv: 'discharge', name: 'Discharges' }, { abrv: 'reviewed-clinical', name: 'Clinical Reviewed' }];
    questions = [{ id: "0", name: "Insurance questions" }, { id: "1", name: "Product questions" }];
    statuses = [{ id: "Hospital" }, { id: "Discharged" }, { id: "Expired" }, { id: "Active" }];

    constructor(rootStore) {
        super(rootStore);

        this.id = rootStore.routerStore.routerState.params.id;
        this.isAdmin = this.rootStore.userStore.user.roles.includes("Administrators");
        this.pageName = 'cr';
        this.nikoHealthBaseUrl = rootStore.applicationStore.app.baasic.settings.nikoHealthBaseUrl;

        this.facilityOrderService = new FacilityOrderService(rootStore.application.baasic.apiClient);
        this.lookupService = new LookupService(rootStore.application.baasic.apiClient);
        this.itemCategoryService = new ItemCategoryService(rootStore.application.baasic.apiClient);
        this.nikoPatientDetailService = new NikoPatientDetailService(rootStore.application.baasic.apiClient);
        this.patientTemplateService = new NikoPatientTemplateService(rootStore.application.baasic.apiClient);
        this.patientTemplateItemService = new NikoPatientTemplateItemService(rootStore.application.baasic.apiClient);
        this.facilityOrderStatusService = new FacilityOrderStatusService(rootStore.application.baasic.apiClient);
        this.itemSubcategoryService = new ItemSubcategoryService(rootStore.application.baasic.apiClient);
        this.historyService = new HistoryService(rootStore.application.baasic.apiClient);

        this.diagnosisCodeService = new DiagnosisCodeService(rootStore.application.baasic.apiClient);
        this.localDoctorService = new LocalDoctorService(rootStore.application.baasic.apiClient);
        this.missingDocumentService = new MissingDocumentService(rootStore.application.baasic.apiClient);
        this.clinicalStatusService = new ClinicalStatusService(rootStore.application.baasic.apiClient);
        this.eligibilityStatusService = new EligibilityStatusService(rootStore.application.baasic.apiClient);
        this.documentationRequestService = new DocumentationRequestService(rootStore.application.baasic.apiClient);

        this.questionsStore = new BaasicDropdownStore(
            { multi: true, filterable: false },
            {
                fetchFunc: () => {
                    return this.questions;
                },
            }
        );
        this.itemCategoryStore = new BaasicDropdownStore(
            { multi: true, filterable: true, initFetch: false },
            {
                fetchFunc: async (term) => {
                    const response = await this.itemCategoryService.find({ searchQuery: term, rpp: 100 });
                    return response.data.item.filter(i => this.facilityOrder.isEnteralWeek ? i.abrv === "enteral" : (i.abrv === "enteral" || i.abrv === "respiratory" || i.abrv === "urological" || i.abrv === "ostomy"));
                },
            }
        );

        this.statusConfirmModal = new ModalParams({ onClose: null });
        this.statusConfirmModal.onConfirm = async () => { await this.setClinicalReviewStarted(); this.statusConfirmModal.close(); };
        this.statusConfirmModal.onCancel = () => { this.statusConfirmModal.close() };
        this.statusConfirmModal.yesLabel = 'MODAL.START_ANYWAYS_LABEL';
        this.statusConfirmModal.noLabel = 'MODAL.CANCEL_LABEL';
        this.statusConfirmModal.swapButtons = true;

        this.nikoPatientInsuranceQuestionModal = new ModalParams({});
        this.nikoPatientInsuranceInfoNoteModal = new ModalParams({});
        this.nikoPatientEligibilityStatusNoteModal = new ModalParams({});
        this.nikoTemplateCreateModal = new ModalParams({});
        this.nikoTemplateMissingProductNoteModal = new ModalParams({});
        this.nikoTemplateInvoiceNoteModal = new ModalParams({});
        this.patientTemplateDoNotSupplyModal = new ModalParams({});
        this.patientTemplateDiscontinuedModal = new ModalParams({});
        this.nikoPatientStatusModal = new ModalParams({});
        this.patientTemplateDeleteModal = new ModalParams({});
        this.nikoPatientTemplateItemCalculationModal = new ModalParams({});
        this.nikoFacilityOrderReasonForChangeModal = new ModalParams({});
        this.nikoPatientTemplateItemNewFlowDeleteQuestionModal = new ModalParams({});
        this.patientTemplateNoteModal = new ModalParams({});

        this.templateChangeConfirmModal = new ModalParams({ onClose: null });
        this.templateChangeConfirmModal.onConfirm = async () => { this.templateChangeConfirmModal.close(); };
        this.templateChangeConfirmModal.yesLabel = 'MODAL.ACKNOWLEDGE';
        this.templateChangeConfirmModal.doNotShowCancel = true;

        this.documentationRequestConfirmModal = new ModalParams({ onClose: null });
        this.documentationRequestConfirmModal.onConfirm = () => { this.openDocumentationRequestWindow(); this.documentationRequestConfirmModal.close(); };
        this.documentationRequestConfirmModal.onCancel = () => { this.openFacilityMissingDocumentsWindow(); this.documentationRequestConfirmModal.close(); };
        this.documentationRequestConfirmModal.yesLabel = 'Proceed with Email';
        this.documentationRequestConfirmModal.noLabel = 'View Facility Missing Docs';
        this.documentationRequestConfirmModal.swapButtons = true;

        // ISG-2781: warn before creating an invoice for a template that has no Niko Sales Order, because Niko cannot
        // link the two afterwards.
        this.invoiceSalesOrderLinkConfirmModal = new ModalParams({ onClose: null });
        this.invoiceSalesOrderLinkConfirmModal.onCancel = () => { this.invoiceSalesOrderLinkConfirmModal.close(); };

        this.nikoFacilityOrderNewFlowExtraSuppliesAddModal = new ModalParams({});
        this.nikoFacilityOrderFindAndReplaceModal = new ModalParams({});
        this.patientTemplateClinicalStatusNoteModal = new ModalParams({});
        this.nikoTemplateMissingDocumentNoteModal = new ModalParams({});
        this.templatePNMissingInformationModal = new ModalParams({});
        this.nikoCreateNewPatientModal = new ModalParams({});

        document.addEventListener('click', (e) => {
            if (!this.templateExpanded.id) return;
            const elements = [
                ...document.querySelectorAll(`[id="templateQuantity-${this.templateExpanded.id}"]`),
                ...document.querySelectorAll(`[id="orderQuantity-${this.templateExpanded.id}"]`),
            ];
            const clickedOutside = elements.length === 0 || elements.every((el) => !el.contains(e.target));
            if (clickedOutside) {
                this.templateExpanded.expanded = false;
            }
        });

        this.statusStore = new BaasicDropdownStore(
            {
                multi: false,
                filterable: false,
                textField: "id"
            },
            {
                fetchFunc: () => {
                    return this.statuses;
                },
            }
        );

        this.queryUtility = new QueryUtility(this.rootStore, async filter => {
            await this.fetchFacilityOrderData(filter);
        }, {});

        const userRoles = this.rootStore.userStore.applicationUser.roles;
        this.isHistoryVisible = !(userRoles.includes("InitialIntake") || userRoles.includes("InsuranceReview"));
        this.isImportAllowed = userRoles.includes("Administrators");

        this.fetch([
            this.initialize()
        ]);
    }

    async initialize() {
        await Promise.all([this.getFacilityOrder(), this.getLookups()]);
        await this.getData();
    }

    @action.bound
    async getFacilityOrder() {
        const facilityOrder = (await this.facilityOrderService.get(this.id, { embed: 'Facility,FacilityOrderStatuses' })).data;
        this.rootStore.titleStore.setTitle(`Clinical Review - ${facilityOrder.facility.name}`);
        const hasMissingDocuments = (await this.documentationRequestService.checkNikoMissingDocuments({ facilityOrderId: facilityOrder.id })).data;
        runInAction(() => {
            this.facilityOrder = facilityOrder;
            this.isInventoryRequestSent = facilityOrder.isInventoryRequestSent;
            this.isDocumentationRequestSent = facilityOrder.isDocumentationRequestSent;
            this.hasMissingDocuments = hasMissingDocuments;
        });
    }

    @action.bound
    async getLookups() {
        this.lookups = await this.lookupService.getLookups("FacilityScheduleType,NewFlowStatus,ImportHeaderStatus,ImportAction,ItemCategory,SubCategory,SpecialtyType,DefaultItemType");
        this.orderingTypeId = this.lookups.facilityScheduleType.find((s) => s.abrv === 'ordering').id;
        this.inProgressStatusId = this.lookups.newFlowStatus.find((s) => s.abrv === 'in-progress').id;
        this.changeStatusId = this.lookups.newFlowStatus.find((s) => s.abrv === 'change').id;
        this.noChangeStatusId = this.lookups.newFlowStatus.find((s) => s.abrv === 'no-change').id;
        this.dischargeStatusId = this.lookups.newFlowStatus.find((s) => s.abrv === 'discharge').id;
        this.activeNewFlowStatusIds = `${this.inProgressStatusId},${this.changeStatusId},${this.noChangeStatusId}`;

        this.insertImportActionId = this.lookups.importAction.find((s) => s.abrv === 'insert').id;
        this.deleteImportActionId = this.lookups.importAction.find((s) => s.abrv === 'delete').id;
        this.updateImportActionId = this.lookups.importAction.find((s) => s.abrv === 'update').id;

        this.ostomyItemCategoryId = this.lookups.itemCategory.find((s) => s.abrv === 'ostomy').id;
        this.enteralItemCategoryId = this.lookups.itemCategory.find((s) => s.abrv === 'enteral').id;
        const ostomySubCategory = this.lookups.subCategory.find((s) => s.abrv === 'Ostomy');
        this.ostomySubCategoryId = ostomySubCategory ? ostomySubCategory.id : null;
        this.trachTubeSubCategoryId = this.lookups.subCategory.find((s) => s.abrv === 'Trach-Tube').id;

        this.syringeDefaultItemTypeId = this.lookups.defaultItemType.find((s) => s.abrv === 'syringe').id;

        const [clinicalResult, masterClinicalResult, missingDocumentResult, eligibilityStatusResult] = await Promise.all([
            this.clinicalStatusService.find({ rpp: 50, isMasterPatient: false }),
            this.clinicalStatusService.find({ rpp: 50, isMasterPatient: true }),
            this.missingDocumentService.find({ rpp: 100, isActive: true, sort: 'name|asc' }),
            this.eligibilityStatusService.find({ rpp: 100 }),
        ]);
        this.clinicalStatuses = clinicalResult.data.item;
        this.masterPatientClinicalStatuses = masterClinicalResult.data.item;
        this.missingDocuments = missingDocumentResult.data.item;
        this.eligibilityStatuses = eligibilityStatusResult.data.item;
    }

    @action.bound
    async getData() {
        this.page = "clinical-review";
        const checkInProgress = (this.clinicalReviewStatus && this.clinicalReviewStatus.completedOn) ? this.hasAnyTemplateInProgress() : Promise.resolve(false);
        const [inventoryResult, , hasInProgress] = await Promise.all([
            this.facilityOrderService.isNikoInventoryStepAllowed(this.id),
            this.itemCategoryStore.filterAsync(),
            checkInProgress,
        ]);
        this.isInventoryStepAllowed = inventoryResult.data;

        const queryParams = this.rootStore.routerStore.routerState.queryParams;
        const openReviewedClinicalTab = (this.clinicalReviewStatus && this.clinicalReviewStatus.completedOn) && !hasInProgress;

        const selectedStatusAbrv = queryParams && queryParams.intakeStatusIds
            ? (_.find(this.lookups.newFlowStatus, s => s.id === queryParams.intakeStatusIds) || {}).abrv || (openReviewedClinicalTab ? 'reviewed-clinical' : 'in-progress')
            : openReviewedClinicalTab ? 'reviewed-clinical' : 'in-progress';

        this.setStatus(selectedStatusAbrv);
    }

    @action.bound
    async hasAnyTemplateInProgress() {
        let params = {
            facilityOrderId: this.id,
            intakeStatusIds: this.activeNewFlowStatusIds,
            isUsIntakeReviewed: true,
            isClinicalReviewed: false,
            pageSize: 1,
            fields: 'id',
        }
        return (await this.patientTemplateService.find(params)).data.item.some(x => x.id);
    }

    @action.bound
    setStatus(statusAbrv) {
        if (this.selectedStatusAbrv == statusAbrv || this.fetchingData) {
            return;
        }
        this.selectedStatusAbrv = statusAbrv;
        this.queryUtility.filter.set('intakeStatusIds', this.selectedStatusAbrv === "discharge" ? this.dischargeStatusId : this.activeNewFlowStatusIds);
        this.queryUtility.fetch();
    }

    buildPatientTemplateQueryParams(additionalParams = {}) {
        const itemCategoryIds = _.map(this.itemCategories, i => i.id);
        return {
            ...additionalParams,
            facilityOrderId: this.id,
            isUsIntakeReviewed: true,
            isClinicalReviewed: this.selectedStatusAbrv == "in-progress" ? false : this.selectedStatusAbrv == "reviewed-clinical" ? true : undefined,
            itemCategoryIds: itemCategoryIds.length > 0 ? itemCategoryIds : undefined,
            showInsuranceQuestionPatients: this.selectedStatusAbrv != "discharge" && this.selectedQuestions.some(x => x.id === "0"),
            showMissingProductNoteTemplates: this.selectedStatusAbrv != "discharge" && this.selectedQuestions.some(x => x.id === "1"),
            pageSize: 10000,
            embed: "NikoPatientDetail,PatientTemplateItems,SubCategory,NikoPatientDetail.EligibilityStatus,NikoPatientDetail.NikoPatientDetailInsurances,OrderingDoctor,PatientTemplateDiagnosisCodes,PatientTemplateMissingDocuments,ClinicalStatus,ItemCategory",
            orderBy: "SubCategory",
            orderDirection: "asc",
        };
    }

    shouldAutoExpandTemplateItems(patientTemplate) {
        return this.selectedStatusAbrv == "in-progress" && (patientTemplate.isNew || patientTemplate.intakeStatusId === this.changeStatusId);
    }

    processPatientTemplateResponse(responseData, existingTemplatesMap = {}, applyAutoExpand = false) {
        const groupedByPatientDetail = _.groupBy(responseData, t => t.nikoPatientDetail.id);
        const nikoPatientDetails = Object.values(groupedByPatientDetail).map(patientTemplates => ({
            ...patientTemplates[0].nikoPatientDetail,
            nikoPatient: { ...patientTemplates[0].nikoPatientDetail.nikoPatient, dateOfBirth: toUTCDate(patientTemplates[0].nikoPatientDetail.nikoPatient.dateOfBirth) },
            disabled: this.selectedStatusAbrv == "in-progress" && patientTemplates[0].nikoPatientDetail.status === 'Hospital',
            patientTemplates: patientTemplates.map(t => ({
                ...t,
                nikoPatientDetail: undefined,
                disabled: false,
                itemsVisible: applyAutoExpand ? this.shouldAutoExpandTemplateItems(t) : false,
                clinicalStoresInitialized: false,
                thirdRowStoresInitialized: false,
                showThirdRow: false,
                tempSalesOrderNote: t.salesOrderNote,
                tempExpirationDate: t.expirationDate,
                history: { userName: '', eventDate: null, fieldName: '', action: '' },
            }))
        }));

        nikoPatientDetails.forEach(nikoPatientDetail => {
            nikoPatientDetail.tempInsuranceInfoNote = nikoPatientDetail.insuranceInfoNote;
            nikoPatientDetail.history = nikoPatientDetail.history || { userName: '', eventDate: null, fieldName: '', action: '' };
            this.setHasHistory(nikoPatientDetail);
            this.getPatientStatusButtonClassName(nikoPatientDetail);
            nikoPatientDetail.patientTemplates.forEach(template => {
                template.tempNote = template.note;

                const existing = existingTemplatesMap[template.id];
                if (existing) {
                    template.itemsVisible = existing.itemsVisible;
                    template.showThirdRow = existing.showThirdRow;
                    template.thirdRowStoresInitialized = existing.thirdRowStoresInitialized;
                    if (existing.thirdRowStoresInitialized) {
                        for (let i = 1; i <= 8; i++) {
                            template[`dxCode${i}Store`] = existing[`dxCode${i}Store`];
                            // Update store value from refreshed data
                            const dxCode = template.patientTemplateDiagnosisCodes && template.patientTemplateDiagnosisCodes.find(x => x.order == i);
                            template[`dxCode${i}Store`].setValue(dxCode ? dxCode.diagnosisCode : null);
                        }
                        template.doctorStore = existing.doctorStore;
                        // Update doctor store value from refreshed data
                        if (template.orderingDoctor) {
                            template.doctorStore.setValue({ ...template.orderingDoctor, orderingDoctor: template.orderingDoctor.name + " - " + template.orderingDoctor.npi + (template.orderingDoctor.primaryAddressState ? " - " + template.orderingDoctor.primaryAddressState : "") });
                        } else {
                            template.doctorStore.setValue(null);
                        }
                    }
                }

                template.patientTemplateItems && template.patientTemplateItems.forEach(item => {
                    item.savedQuantity = item.quantity;
                    item.savedSalesOrderQuantity = item.salesOrderQuantity;
                    item.savedNote = item.note;
                });
                template.isDifferentQty = template.patientTemplateItems && template.patientTemplateItems.some(x => x.quantity != x.salesOrderQuantity);
                this.initializeClinicalStores(template, nikoPatientDetail);
            });
        });

        return nikoPatientDetails;
    }

    @action.bound
    async fetchFacilityOrderData(filter) {
        runInAction(() => {
            this.fetchingData = true;
        });
        const params = this.buildPatientTemplateQueryParams(filter);
        const responseData = (await this.patientTemplateService.find(params)).data.item;
        await this.attachNikoInvoiceIds(responseData);
        const nikoPatientDetails = this.processPatientTemplateResponse(responseData, {}, true);

        if (this.clinicalReviewStatus && !this.clinicalReviewStatus.completedOn && this.selectedStatusAbrv != "reviewed-clinical") {
            this.checkIsReviewed(false);
        }

        runInAction(() => {
            this.nikoPatientDetails = nikoPatientDetails;
            this.isEmpty = this.selectedStatusAbrv == "in-progress" && this.clinicalReviewStatus && !this.clinicalReviewStatus.completedOn && this.nikoPatientDetails.length == 0;
        });

        if (((this.selectedStatusAbrv != "discharge" && this.selectedQuestions.some(x => x.id)) || this.itemCategories.some(x => x.id)) && this.nikoPatientDetails.length === 0) {
            this.rootStore.notificationStore.warning("No templates found under selected filter");
        }
        runInAction(() => {
            this.fetchingData = false;
        });
    }

    @action.bound
    async fetchPatientTemplateByPatient(nikoPatientDetailId) {
        if (this.clinicalReviewStatus && !this.clinicalReviewStatus.completedOn && this.selectedStatusAbrv != "reviewed-clinical") {
            this.checkIsReviewed(false);
        }

        let index = this.nikoPatientDetails.findIndex(x => x.id == nikoPatientDetailId);
        if (index == -1) {
            await this.queryUtility.fetch();
            return;
        }

        // Build map of existing UI state to preserve
        const existingPatientDetail = this.nikoPatientDetails[index];
        const existingTemplatesMap = {};
        existingPatientDetail.patientTemplates.forEach(t => {
            existingTemplatesMap[t.id] = t;
        });

        const params = this.buildPatientTemplateQueryParams({
            intakeStatusIds: this.queryUtility.filter.intakeStatusIds,
            nikoPatientDetailId: nikoPatientDetailId,
        });
        const responseData = (await this.patientTemplateService.find(params)).data.item;
        await this.attachNikoInvoiceIds(responseData);
        const nikoPatientDetails = this.processPatientTemplateResponse(responseData, existingTemplatesMap);

        index = this.nikoPatientDetails.findIndex(x => x.id == nikoPatientDetailId);
        if (index == -1)
            return;

        if (nikoPatientDetails && nikoPatientDetails[0]) {
            runInAction(() => {
                const newTemplates = nikoPatientDetails[0].patientTemplates;
                const existingTemplates = this.nikoPatientDetails[index].patientTemplates;

                const ordered = newTemplates.map(newTemplate => {
                    const existingTemplate = existingTemplates.find(t => t.id === newTemplate.id);
                    if (existingTemplate) {
                        Object.assign(existingTemplate, newTemplate);
                        return existingTemplate;
                    }
                    return newTemplate;
                });

                existingTemplates.splice(0, existingTemplates.length, ...ordered);

                nikoPatientDetails[0].patientTemplates = existingTemplates;
                Object.assign(this.nikoPatientDetails[index], nikoPatientDetails[0]);
            });
        }
        else {
            runInAction(() => {
                this.nikoPatientDetails.splice(index, 1);
            });
        }

        runInAction(() => {
            this.isEmpty = this.selectedStatusAbrv == "in-progress" && this.clinicalReviewStatus && !this.clinicalReviewStatus.completedOn && this.nikoPatientDetails.length == 0;
        });
    }

    @action.bound
    async fetchPatientTemplateItemsById(nikoPatientDetailId, patientTemplateId) {
        const index = this.nikoPatientDetails.findIndex(x => x.id == nikoPatientDetailId);
        if (index == -1)
            return;
        const patientTemplate = this.nikoPatientDetails[index].patientTemplates && this.nikoPatientDetails[index].patientTemplates.find(x => x.id == patientTemplateId);
        if (patientTemplate && patientTemplate.patientTemplateItems) {
            let responseData = (await this.patientTemplateService.get(patientTemplateId, { embed: 'PatientTemplateItems' })).data;
            if (responseData.patientTemplateItems) {
                runInAction(() => {
                    Object.assign(patientTemplate.patientTemplateItems, responseData.patientTemplateItems);
                    patientTemplate.patientTemplateItems.forEach(item => {
                        item.savedQuantity = item.quantity;
                        item.savedSalesOrderQuantity = item.salesOrderQuantity;
                        item.savedNote = item.note;
                    });
                    patientTemplate.isDifferentQty = patientTemplate.patientTemplateItems.some(x => x.quantity != x.salesOrderQuantity);
                });
            }
        }
    }

    @action.bound
    setHasHistory(nikoPatientDetail) {
        this.createInsuranceCompareModel(nikoPatientDetail);
        nikoPatientDetail.hasHistory = !nikoPatientDetail.isNew &&
            (
                (nikoPatientDetail.primaryActive.insuranceId.current != nikoPatientDetail.primaryActive.insuranceId.initial || nikoPatientDetail.primaryActive.policyNo.current != nikoPatientDetail.primaryActive.policyNo.initial) ||
                (nikoPatientDetail.secondaryActive.insuranceId.current != nikoPatientDetail.secondaryActive.insuranceId.initial || nikoPatientDetail.secondaryActive.policyNo.current != nikoPatientDetail.secondaryActive.policyNo.initial) ||
                (nikoPatientDetail.primaryInactive.insuranceId.current && (nikoPatientDetail.primaryInactive.insuranceId.current != nikoPatientDetail.primaryInactive.insuranceId.initial || nikoPatientDetail.primaryInactive.policyNo.current != nikoPatientDetail.primaryInactive.policyNo.initial)) ||
                (nikoPatientDetail.secondaryInactive.insuranceId.current && (nikoPatientDetail.secondaryInactive.insuranceId.current != nikoPatientDetail.secondaryInactive.insuranceId.initial || nikoPatientDetail.secondaryInactive.policyNo.current != nikoPatientDetail.secondaryInactive.policyNo.initial)) ||
                (nikoPatientDetail.eligibilityStatusId != (nikoPatientDetail.initialNikoPatientDetail ? nikoPatientDetail.initialNikoPatientDetail.EligibilityStatusId : "")) ||
                nikoPatientDetail.patientTemplates.some(pt => !pt.isNew && pt.isPriorAuthNeeded != (pt.initialPatientTemplate && pt.initialPatientTemplate.IsPriorAuthNeeded))
            );
    }

    @action.bound
    createInsuranceCompareModel(nikoPatientDetail) {
        nikoPatientDetail.primaryActive = {
            id: { initial: null, current: null },
            insuranceId: { initial: null, current: null },
            insurance: { initial: null, current: null },
            policyNo: { initial: null, current: null },
            startDate: { initial: null, current: null },
            endDate: { initial: null, current: null },
        };
        nikoPatientDetail.primaryInactive = {
            id: { initial: null, current: null },
            insuranceId: { initial: null, current: null },
            insurance: { initial: null, current: null },
            policyNo: { initial: null, current: null },
            startDate: { initial: null, current: null },
            endDate: { initial: null, current: null },
        };
        nikoPatientDetail.secondaryActive = {
            id: { initial: null, current: null },
            insuranceId: { initial: null, current: null },
            insurance: { initial: null, current: null },
            policyNo: { initial: null, current: null },
            startDate: { initial: null, current: null },
            endDate: { initial: null, current: null },
        };
        nikoPatientDetail.secondaryInactive = {
            id: { initial: null, current: null },
            insuranceId: { initial: null, current: null },
            insurance: { initial: null, current: null },
            policyNo: { initial: null, current: null },
            startDate: { initial: null, current: null },
            endDate: { initial: null, current: null },
        };

        const fill = (list, targetKey) => {
            if (!list) return;
            const map = targetKey === 'current' ?
                { type: 'type', status: 'status', id: 'id', insuranceId: 'insuranceId', insurance: 'insurance', policyNo: 'policyNo', startDate: 'startDate', endDate: 'endDate' }
                :
                { type: 'Type', status: 'Status', id: 'Id', insuranceId: 'InsuranceId', insurance: 'Insurance', policyNo: 'PolicyNo', startDate: 'StartDate', endDate: 'EndDate' };

            list.forEach(i => {
                const key = `${i[map.type].toLowerCase()}${i[map.status]}`;
                const target = nikoPatientDetail[key];
                if (!target) return;
                target.id[targetKey] = i[map.id];
                target.insuranceId[targetKey] = i[map.insuranceId];
                target.insurance[targetKey] = i[map.insurance];
                target.policyNo[targetKey] = i[map.policyNo];
                target.startDate[targetKey] = i[map.startDate];
                target.endDate[targetKey] = i[map.endDate];
            });
        };

        fill(nikoPatientDetail.nikoPatientDetailInsurances, 'current');
        if (nikoPatientDetail.initialNikoPatientDetail) {
            fill(nikoPatientDetail.initialNikoPatientDetail.NikoPatientDetailInsurances, 'initial');
            resolveInitialEligibilityStatus(nikoPatientDetail.initialNikoPatientDetail, this.eligibilityStatuses);
        }
    }

    @action.bound
    getPatientStatusButtonClassName(nikoPatientDetail, status = null) {
        nikoPatientDetail.hospitalClassName = "";
        nikoPatientDetail.dischargedClassName = "";
        nikoPatientDetail.expiredClassName = "";
        nikoPatientDetail.activeClassName = "";

        this.statuses.filter(i => !status || i.id === status).forEach((item) => {
            if (this.selectedStatusAbrv === "in-progress") {
                if (item.id === "Hospital" && nikoPatientDetail.status === 'Hospital') {
                    nikoPatientDetail.hospitalClassName = "is-active--alt"
                }
                else if (item.id === "Active" && nikoPatientDetail.status !== 'Hospital') {
                    nikoPatientDetail.activeClassName = "is-active";
                }
            }
            else if (this.selectedStatusAbrv === "discharge") {
                if (item.id === "Hospital" && nikoPatientDetail.status === 'Hospital') {
                    nikoPatientDetail.hospitalClassName = "is-active";
                }
                else if (item.id === "Discharged" && nikoPatientDetail.status === 'Discharged') {
                    nikoPatientDetail.dischargedClassName = "is-active";
                }
                else if (item.id === "Expired" && nikoPatientDetail.status === 'Expired') {
                    nikoPatientDetail.expiredClassName = "is-active";
                }
                else if (item.id === "Active" && nikoPatientDetail.status !== 'Hospital' && nikoPatientDetail.status !== 'Discharged' && nikoPatientDetail.status !== 'Expired') {
                    nikoPatientDetail.activeClassName = "is-active";
                }
            }
            else if (this.selectedStatusAbrv === "reviewed-clinical" && item.id === "Active") {
                nikoPatientDetail.activeClassName = "is-active";
            }
        });
    }

    @action.bound
    async goToFacilityPreview() {
        window.open(`${this.rootStore.routerStore.findRoute('master.app.main.facility.preview').pattern.replace(":id", this.facilityOrder.facilityId)}`);
    }

    @action.bound
    async goToFacilityCalendar() {
        window.open(`${this.rootStore.routerStore.findRoute('master.app.main.schedule.calendar').pattern}?facilityId=${this.facilityOrder.facilityId}&calendarTypeId=${this.orderingTypeId}`);
    }

    // --- Clinical Review Status Management ---

    @action.bound
    async startClinicalReview() {
        await this.getFacilityOrderStatus();
        if (!(this.usIntakeStatus && this.usIntakeStatus.completedOn)) {
            this.statusConfirmModal.open({
                message: "US Intake has not yet completed their flow. You can start Clinical Review, but will not be able to complete it until US Intake completes their flow. Please note that you will not see any templates that US Intake hasn't reviewed yet."
            });
        }
        else {
            await this.setClinicalReviewStarted();
        }
    }

    @action.bound
    async setClinicalReviewStarted() {
        this.disabled = true;
        try {
            const status = this.lookups.importHeaderStatus.find(i => i.abrv === 'clinical-review');
            await this.facilityOrderStatusService.create({ facilityOrderId: this.facilityOrder.id, statusId: status.id, startedOn: new Date(), startedByUserId: this.rootStore.userStore.user.id });
        }
        catch (err) {
            this.rootStore.notificationStore.error("The facility order status has already been updated. Please reload the page.");
        }
        finally {
            await this.getFacilityOrderStatus();
            await this.checkIsReviewed();
            this.disabled = false;
        }
    }

    @action.bound
    async setClinicalReviewCompleted() {
        if (!this.clinicalReviewStatus) {
            return;
        }

        this.disabled = true;
        try {
            const request = { ...this.clinicalReviewStatus, completedOn: new Date(), completedByUserId: this.rootStore.userStore.user.id };
            await this.facilityOrderStatusService.update(request);
        }
        catch (err) {
            this.rootStore.notificationStore.error("The facility order status has already been updated. Please reload the page.");
        }
        finally {
            await this.getFacilityOrderStatus();
            await this.checkIsReviewed();
            this.disabled = false;
        }
    }

    @action.bound
    async getFacilityOrderStatus() {
        const statuses = await this.facilityOrderStatusService.find({ facilityOrderId: this.facilityOrder.id, embed: "Status,StartedByUser,CompletedByUser" });
        runInAction(() => {
            this.facilityOrder.facilityOrderStatuses.replace(statuses.data.item);
        });
        this.refreshKey = uuid();
    }

    // --- Review Templates ---

    @action.bound
    async reviewPatientTemplates(nikoPatientDetail, patientTemplateId, isReview) {
        nikoPatientDetail.disabled = true;
        try {
            const updatedItems = _.map(nikoPatientDetail.patientTemplates.filter((pt) => pt.id && (!patientTemplateId || pt.id == patientTemplateId)), (pt) => {
                return {
                    id: pt.id,
                    isReviewed: isReview,
                    pageName: this.pageName,
                };
            });
            await this.patientTemplateService.reviewPatientTemplate(updatedItems);
            this.fetchPatientTemplateByPatient(nikoPatientDetail.id);
        }
        catch (err) {
            this.rootStore.notificationStore.error("An unexpected error occurred while trying to review the template.");
        }
        finally {
            nikoPatientDetail.disabled = false;
        }
    }

    @action.bound
    async createNikoSOs() {
        if (this.creatingNikoSOs) return;
        this.creatingNikoSOs = true;
        try {
            this.rootStore.notificationStore.warning("Creating Niko Sales Orders is in progress.");
            await this.facilityOrderService.createNikoSOs(this.id);
            this.rootStore.notificationStore.success("Sales Orders created.");
            await this.queryUtility.fetch();
        } catch (err) {
            if (err.data && err.data.errors) {
                this.rootStore.notificationStore.error(err.data.errors);
            } else if (err.data) {
                this.rootStore.notificationStore.error(err.data);
            } else {
                this.rootStore.notificationStore.error("Error occurred while trying to create Niko Sales Orders.");
            }
        } finally {
            this.creatingNikoSOs = false;
        }
    }

    @action.bound
    async createNikoInvoices() {
        if (this.creatingNikoInvoices) return;
        // ISG-2279: Invoice import cannot be started before the Facility Order is sent.
        if (!this.facilityOrder || !this.facilityOrder.sendOrder) {
            this.rootStore.notificationStore.error("Invoice import cannot be started before the order is sent.");
            return;
        }
        this.creatingNikoInvoices = true;
        try {
            this.rootStore.notificationStore.warning("Creating Niko Invoices is in progress.");
            await this.facilityOrderService.createNikoInvoices(this.id);
            this.rootStore.notificationStore.success("Niko invoice import completed.");
            await this.queryUtility.fetch();
        } catch (err) {
            if (err.data && err.data.errors) {
                this.rootStore.notificationStore.error(err.data.errors);
            } else if (err.data) {
                this.rootStore.notificationStore.error(err.data);
            } else {
                this.rootStore.notificationStore.error("Error occurred while trying to create Niko Invoices.");
            }
        } finally {
            this.creatingNikoInvoices = false;
        }
    }

    @action.bound
    async importToNiko(patientTemplateId, nikoPatientDetailId) {
        this.importingToNikoTemplateIds.push(patientTemplateId);
        try {
            this.rootStore.notificationStore.warning("Creating Niko Sales Order is in progress.");
            await this.patientTemplateService.nikoProcess({ id: patientTemplateId });
            this.rootStore.notificationStore.success("Sales Order created successfully.");
            this.fetchPatientTemplateByPatient(nikoPatientDetailId);
        } catch (err) {
            if (err.data && err.data.errors) {
                this.rootStore.notificationStore.error(err.data.errors);
            } else {
                this.rootStore.notificationStore.error("Error occurred while trying to create Niko Sales Order.");
            }
        } finally {
            this.importingToNikoTemplateIds.remove(patientTemplateId);
        }
    }

    @action.bound
    async createNikoInvoice(patientTemplateId, nikoPatientDetailId) {
        this.importingToNikoTemplateIds.push(patientTemplateId);
        try {
            this.rootStore.notificationStore.warning("Creating Niko invoice is in progress.");
            await this.patientTemplateService.nikoInvoice({ id: patientTemplateId });
            this.rootStore.notificationStore.success("Invoice created successfully.");
            this.fetchPatientTemplateByPatient(nikoPatientDetailId);
        } catch (err) {
            if (err.data && err.data.errors) {
                this.rootStore.notificationStore.error(err.data.errors);
            } else {
                this.rootStore.notificationStore.error("Error occurred while trying to create Niko invoice.");
            }
        } finally {
            this.importingToNikoTemplateIds.remove(patientTemplateId);
        }
    }

    // ISG-2781: template-level invoice import for a template with no Niko Sales Order is allowed, but the invoice
    // cannot be linked to the Sales Order afterwards, so the user has to confirm it first.
    @action.bound
    async confirmCreateNikoInvoice(patientTemplate, nikoPatientDetailId) {
        if (patientTemplate.nikoOrderId) {
            await this.createNikoInvoice(patientTemplate.id, nikoPatientDetailId);
            return;
        }

        this.invoiceSalesOrderLinkConfirmModal.onConfirm = async () => {
            this.invoiceSalesOrderLinkConfirmModal.close();
            await this.createNikoInvoice(patientTemplate.id, nikoPatientDetailId);
        };
        this.invoiceSalesOrderLinkConfirmModal.open({
            message: "This PSO does not have a linked sales order. If the invoice is created prior to the sales order, it will not be linked. Are you sure you want to proceed?"
        });
    }

    // --- Reviewed Check ---

    @action.bound
    async checkIsReviewed(setStatus = true) {
        let params = {
            facilityOrderId: this.id,
            isUsIntakeReviewed: true,
            isClinicalReviewed: false,
            pageSize: 1,
            fields: 'id',
        }
        const isReviewed = !(await this.patientTemplateService.find(params)).data.item.some(x => x.id);
        let isDocumentationRequestSent;
        let hasMissingDocuments;
        if (isReviewed) {
            isDocumentationRequestSent = (await this.facilityOrderService.hasSentEmail(this.facilityOrder.id, "DocumentationRequest")).data;
            hasMissingDocuments = (await this.documentationRequestService.checkNikoMissingDocuments({ facilityOrderId: this.facilityOrder.id })).data;
        }
        runInAction(() => {
            this.isReviewed = isReviewed;
            if (isReviewed) {
                this.isDocumentationRequestSent = isDocumentationRequestSent;
                this.hasMissingDocuments = hasMissingDocuments;
            }
            if (setStatus) {
                this.isEmpty = this.selectedStatusAbrv == "in-progress" && this.clinicalReviewStatus && !this.clinicalReviewStatus.completedOn && this.nikoPatientDetails.length == 0;
            }
        });
    }

    // --- Filters ---

    @action.bound
    filterByQuestion(selected) {
        this.selectedQuestions = selected.value;
        this.queryUtility.fetch();
    }

    @action.bound
    filterByCategory(selected) {
        this.itemCategories = selected.value;
        this.queryUtility.fetch();
    }

    // --- Actions ---

    @action.bound
    insuranceCoverageStatusOverviewEmailEdit() {
        window.open(`${this.rootStore.routerStore
            .findRoute('master.app.main.niko-facility-order.niko-overview.new-flow-edit-email')
            .pattern.replace(":facilityOrderId", this.id)}?isNiko=true
            `);
    }

    @action.bound
    async documentationRequestCreate() {
        const result = await this.documentationRequestService.checkNikoMissingDocuments({ facilityOrderId: this.facilityOrder.id });
        if (result.data) {
            const response = await this.documentationRequestService.hasPreviousDosTemplates({ facilityOrderId: this.facilityOrder.id });
            if (response.data) {
                this.documentationRequestConfirmModal.open({
                    message: "Past templates will be included on this Doc Request Email. Would you like to view them or proceed with generating the email?"
                });
            }
            else {
                this.openDocumentationRequestWindow();
            }
        }
        else {
            this.rootStore.notificationStore.error("There are no missing documents for this Facility order.");
        }
    }

    @action.bound
    openDocumentationRequestWindow() {
        window.open(`${this.rootStore.routerStore
            .findRoute('master.app.main.niko-facility-order.niko-documentation-request')
            .pattern.replace(":facilityOrderId", this.id)}
            `);
    }

    @action.bound
    openFacilityMissingDocumentsWindow() {
        const doesNotMeetStatus = this.clinicalStatuses && this.clinicalStatuses.find(s => s.name === 'Does Not Meet Insurance Criteria');
        const dosDateFrom = moment(this.facilityOrder.dateOfService).subtract(11, 'months').subtract(15, 'days').format('YYYY-MM-DD');
        const dosDateTo = moment(this.facilityOrder.dateOfService).subtract(1, 'days').format('YYYY-MM-DD');
        const params = [
            `facilityId=${this.facilityOrder.facilityId}`,
            `hasMissingDocs=true`,
            `isSystemDns=false`,
            `isUserDns=false`,
            `dosDateFrom=${dosDateFrom}`,
            `dosDateTo=${dosDateTo}`,
            `expandClinical=true`,
        ];
        if (doesNotMeetStatus) {
            params.push(`clinicalStatusId=${doesNotMeetStatus.id}`);
        }
        window.open(`${this.rootStore.routerStore
            .findRoute('master.app.main.sales-order.niko-sales-order')
            .pattern}?${params.join('&')}`, "_blank");
    }

    @action.bound
    downloadInventoryReport() {
        const facilityInventoryCountDetailService = new FacilityInventoryCountDetailService(this.rootStore.application.baasic.apiClient);
        const downloadUrl = `${facilityInventoryCountDetailService.getDownloadNikoInventoryReportUrl({ facilityOrderId: this.facilityOrder.id })}&contentDisposition=attachment&token=${this.rootStore.authStore.token.tokenUrl}&ttype=${this.rootStore.authStore.token.type}`;
        window.open(downloadUrl, "_blank");
    }

    @action.bound
    orderHistory() {
        window.open(this.rootStore.routerStore.findRoute("master.app.main.niko-facility-order.history").pattern.replace(":id", this.facilityOrder.id));
    }

    // --- Create New Patient ---

    isCheckingDeliveryLocation = false;

    @action.bound
    async openCreateNewPatientModal() {
        if (this.disabledByStatus) {
            return this.rootStore.notificationStore.error("This action is not allowed until the Clinical Review is started.");
        }
        if (this.isCheckingDeliveryLocation) {
            return;
        }
        const facilityId = this.facilityOrder ? this.facilityOrder.facilityId : null;
        if (facilityId) {
            this.isCheckingDeliveryLocation = true;
            let loadingToastId = null;
            const loadingToastTimeout = setTimeout(() => {
                loadingToastId = this.rootStore.notificationStore.warning("Checking Facility Delivery Location in Niko...", false);
            }, 1000);
            let hasDeliveryLocation = true;
            try {
                const facilityService = new FacilityService(this.rootStore.application.baasic.apiClient);
                const response = await facilityService.hasNikoDeliveryLocation(facilityId);
                hasDeliveryLocation = !(response && response.data && !response.data.hasDeliveryLocation);
            } finally {
                clearTimeout(loadingToastTimeout);
                if (loadingToastId !== null) {
                    this.rootStore.notificationStore.dismiss(loadingToastId);
                }
                this.isCheckingDeliveryLocation = false;
            }
            if (!hasDeliveryLocation) {
                return this.rootStore.notificationStore.error("Action not allowed: this Facility has no Delivery Location in Niko. Add the Delivery Location in Niko and try again.");
            }
        }
        this.nikoCreateNewPatientModal.open({
            facilityOrderId: this.id,
            facilityOrder: this.facilityOrder,
            facilityId: facilityId,
            fetchPatientTemplates: async () => { this.queryUtility.fetch(); },
            pageName: this.pageName,
            pageType: 'CR',
        });
    }

    // --- Find and Replace ---

    @action.bound
    async openNikoFacilityOrderFindAndReplaceModal() {
        if (this.disabledByStatus) {
            return this.rootStore.notificationStore.error("This action is not allowed until the Clinical Review is started.");
        }

        this.nikoFacilityOrderFindAndReplaceModal.open({
            facilityOrderId: this.facilityOrder.id,
            fetchData: async () => { this.queryUtility.fetch(); }
        });
    }

    // --- SignalR ---

    @action.bound
    async signalRAction(message) {
        if (message.text && message.userId != this.rootStore.userStore.user.id && this.nikoPatientDetails && this.nikoPatientDetails.some(npd => npd.patientTemplates && npd.patientTemplates.some(pt => pt.id == message.id || (pt.patientTemplateItems && pt.patientTemplateItems.some(pti => pti.id == message.id))))) {
            this.notification = message.text;
        }
        else if (message.table == "SE") {
            if (message.column == "EmailType" && message.id == this.facilityOrder.id) {
                if (message.text == "DocumentationRequest") {
                    this.isDocumentationRequestSent = true;
                }
                if (message.text == "InventoryRequest") {
                    this.isInventoryRequestSent = true;
                    this.getFacilityOrderStatus();
                    this.checkIsReviewed();
                }
            }
        }
        else if (message.table === "FOS") {
            await this.getFacilityOrderStatus();
            await this.checkIsReviewed();
        }
        else if (message.table == "ReloadClinicalTemplate" && message.userId == this.rootStore.userStore.user.id) {
            if (message.text == 'Template has been updated.') {
                this.rootStore.notificationStore.success("Facility order template(s) imported successfully.");
            }
            else {
                this.rootStore.notificationStore.error(message.text);
            }
        }
        else if (message.table == "PT" || message.table == "PTI" || message.table == "NPD") {
            if (message.table == "NPD") {
                await this.fetchPatientTemplateByPatient(message.id);
            }
            else if (message.patientId) {
                await this.fetchPatientTemplateByPatient(message.patientId);
            }
            else if (message.table == "PTI" && message.customId) {
                await this.fetchPatientTemplateByPatient(message.customId);
            }
            else {
                this.disabled = true;
                await this.queryUtility.fetch();
                this.disabled = false;
            }
        }
        else if (message.column == "SendOrder") {
            this.facilityOrder.sendOrder = true;
        }
    }

    // --- Patient/Template Management (from US Intake pattern) ---

    isMasterPatient = (nikoPatientDetail) => {
        return this.facilityOrder && this.facilityOrder.facility
            && this.facilityOrder.facility.nikoMasterPatientId
            && this.facilityOrder.facility.nikoMasterPatientId == nikoPatientDetail.nikoPatientId;
    }

    @action.bound
    async openNikoTemplateCreateModal(nikoPatientDetail) {
        const isMasterPatient = this.isMasterPatient(nikoPatientDetail);

        if (isMasterPatient) {
            await this.nikoFacilityOrderNewFlowExtraSuppliesAddModal.open({
                facilityOrderId: this.facilityOrder.id,
                sendAgoraPo: this.facilityOrder.facility.sendAgoraPo,
                skipAutoItems: true,
                fetchData: () => this.fetchPatientTemplateByPatient(nikoPatientDetail.id)
            });
        } else {
            await this.nikoTemplateCreateModal.open({
                nikoPatientDetail: nikoPatientDetail,
                fetchPatientTemplates: () => this.fetchPatientTemplateByPatient(nikoPatientDetail.id),
                pageName: this.pageName,
                tabName: this.selectedStatusAbrv
            });
        }
    }

    @action.bound
    async openNikoPatientStatusModal(nikoPatientDetail, status) {
        this.nikoPatientStatusModal.open({
            status: status,
            nikoPatientDetail: nikoPatientDetail,
            pageName: this.pageName,
            tabName: this.selectedStatusAbrv,
            additionalMessage: this.selectedStatusAbrv == "discharge" && status == 'Active' && this.getDischargeActivationMessage(nikoPatientDetail),
            getNikoPatient: () => this.fetchPatientTemplateByPatient(nikoPatientDetail.id)
        });
    }

    getDischargeActivationMessage(nikoPatientDetail) {
        const baseMessage = "Patient will be intake reviewed upon activation. Please confirm that you've applied all necessary updates to the patient's insurance and templates.";
        if (nikoPatientDetail.eligibilityStatus == null) {
            return baseMessage;
        }
        if (nikoPatientDetail.eligibilityStatus.name === 'Eligible') {
            return `${baseMessage} After activation, the patient's template(s) will be moved to the In Progress tab.`;
        }
        return `${baseMessage} After activation, the patient's template(s) will be moved to the Reviewed tab.`;
    }

    getDischargeTemplateActivationMessage(nikoPatientDetail) {
        const baseMessage = "Template will be intake reviewed upon activation. Please confirm that you've applied all necessary updates to the patient's insurance and templates.";
        if (nikoPatientDetail.eligibilityStatus == null) {
            return baseMessage;
        }
        if (nikoPatientDetail.eligibilityStatus.name === 'Eligible') {
            return `${baseMessage} After activation, the template will be moved to the In Progress tab.`;
        }
        return `${baseMessage} After activation, the template will be moved to the Reviewed tab.`;
    }

    @action.bound
    changeItemsVisibility(patientTemplate) {
        patientTemplate.itemsVisible = !patientTemplate.itemsVisible;
    }

    @action.bound
    async openNikoPatientInsuranceQuestionModal(nikoPatientDetail) {
        this.nikoPatientInsuranceQuestionModal.open({
            nikoPatientDetail: nikoPatientDetail,
        });
    }

    @action.bound
    async openNikoPatientInsuranceInfoNoteModal(nikoPatientDetail) {
        this.nikoPatientInsuranceInfoNoteModal.open({
            nikoPatientDetail: nikoPatientDetail,
            setTempValue: (nikoPatientDetail) => { nikoPatientDetail.tempInsuranceInfoNote = nikoPatientDetail.insuranceInfoNote }
        });
    }

    @action.bound
    async updateNikoPatientInsuranceInfoNote(nikoPatientDetail, value, save = false) {
        nikoPatientDetail.tempInsuranceInfoNote = value;
        if (save) {
            nikoPatientDetail.insuranceInfoNote = nikoPatientDetail.tempInsuranceInfoNote;
            await this.updateInsuranceInfo(nikoPatientDetail);
        }
    }

    @action.bound
    async updateInsuranceInfo(nikoPatientDetail) {
        nikoPatientDetail.disabled = true;
        try {
            await this.nikoPatientDetailService.updatePatientInsuranceInfo(nikoPatientDetail);
        }
        catch (err) {
            this.rootStore.notificationStore.error(getErrorMessage(err, 'Error occurred while trying to update patient Insurance Information.'));
        }
        finally {
            nikoPatientDetail.disabled = false;
        }
    }

    @action.bound
    async openNikoHealthPatient(remoteId) {
        window.open(`${this.rootStore.applicationStore.app.baasic.settings.nikoHealthBaseUrl}v2/#/patients/${remoteId}/demographics`);
    }

    @action.bound
    showPreviousFlag(nikoPatientDetail) {
        return nikoPatientDetail && nikoPatientDetail.initialNikoPatientDetail && (nikoPatientDetail.status != nikoPatientDetail.initialNikoPatientDetail.Status);
    }

    @action.bound
    async openReasonForChangeModal(nikoPatientDetail) {
        this.nikoFacilityOrderReasonForChangeModal.open({
            nikoPatientDetail: nikoPatientDetail,
            getNikoPatient: () => this.fetchPatientTemplateByPatient(nikoPatientDetail.id),
            sendOrder: this.facilityOrder && this.facilityOrder.sendOrder,
        });
    }

    @action.bound
    async openNikoPatientEligibilityStatusNoteModal(nikoPatientDetail) {
        this.nikoPatientEligibilityStatusNoteModal.open({
            nikoPatientDetail: nikoPatientDetail,
        });
    }

    @action.bound
    async fetchPatientTemplateIntakeStatusById(patientTemplate) {
        if (this.selectedStatusAbrv == "discharge" || !patientTemplate) {
            return;
        }
        let responseData = (await this.patientTemplateService.get(patientTemplate.id, { fileds: 'IntakeStatusId' })).data;
        if (patientTemplate.intakeStatusId != responseData.intakeStatusId) {
            patientTemplate.intakeStatusId = responseData.intakeStatusId;
        }
    }

    // --- Template Operations ---

    @action.bound
    async openPatientTemplateMissingProductNoteModal(patientTemplate) {
        this.nikoTemplateMissingProductNoteModal.open({
            patientTemplate: patientTemplate,
        });
    }

    @action.bound
    async openPatientTemplateInvoiceNoteModal(patientTemplate) {
        this.nikoTemplateInvoiceNoteModal.open({
            patientTemplate: patientTemplate,
        });
    }

    @action.bound
    async openPatientTemplateDiscontinuedModal(patientTemplate) {
        const nikoPatientDetail = this.nikoPatientDetails.find(x => x.id == patientTemplate.nikoPatientDetailId);
        const requiresValidation = patientTemplate.isDiscontinued && nikoPatientDetail.status === 'Active';

        this.patientTemplateDiscontinuedModal.open({
            icon: "cross-circle",
            title: `${patientTemplate.isDiscontinued ? "Unmark" : "Mark"} As Discontinued`,
            additionalMessage: requiresValidation
                ? this.getDischargeTemplateActivationMessage(nikoPatientDetail)
                : undefined,
            validate: requiresValidation
                ? () => {
                    if (nikoPatientDetail.eligibilityStatus == null) {
                        this.rootStore.notificationStore.error('Eligibility status must be selected prior to activation.');
                        return false;
                    }
                    if (nikoPatientDetail.eligibilityStatus.name === 'Eligible' && nikoPatientDetail.primaryActive.insuranceId.current == null) {
                        this.rootStore.notificationStore.error('Primary Insurance must be added prior to activation.');
                        return false;
                    }
                    return true;
                }
                : undefined,
            action: () => this.markDiscontinued(patientTemplate),
        });
    }

    @action.bound
    async markDiscontinued(patientTemplate) {
        // ISG-871: the backend runs drainage bag / leg bag validations when activating an Active patient's template and returns warnings.
        const response = await this.patientTemplateService.updateDiscontinuedStatus({ id: patientTemplate.id, isDiscontinued: !patientTemplate.isDiscontinued, pageName: this.pageName });
        await this.fetchPatientTemplateByPatient(patientTemplate.nikoPatientDetailId);

        const validationMessages = response && response.data && response.data.messages;
        if (validationMessages && validationMessages.length) {
            validationMessages.forEach((message) => this.rootStore.notificationStore.warning(message));
        }
    }

    @action.bound
    async openPatientTemplateDoNotSupplyModal(patientTemplate) {
        this.patientTemplateDoNotSupplyModal.open({
            icon: "over-circle",
            title: `${patientTemplate.isUserDns ? "Unmark" : "Mark"} As Do Not Supply`,
            description: patientTemplate.isUserDns ? "Are you sure you want to unmark this order as Do Not Supply?" : undefined,
            action: () => this.markDoNotSupply(patientTemplate)
        });
    }

    @action.bound
    async markDoNotSupply(patientTemplate) {
        patientTemplate.isUserDns = !patientTemplate.isUserDns;
        await this.patientTemplateService.update(patientTemplate);
    }

    @action.bound
    async openPatientTemplateDeleteModal(nikoPatientDetail, patientTemplate) {
        const params = { facilityOrderId: this.id, nikoPatientDetailId: patientTemplate.nikoPatientDetailId, pageSize: 3, fields: 'Id,TemplateId' };
        const response = (await this.patientTemplateService.find(params)).data.item;
        this.patientTemplateDeleteModal.open({
            item: patientTemplate,
            deleteTemplate: () => this.deleteTemplate(patientTemplate),
            numberOfTemplates: response.every(x => x.id == patientTemplate.id || x.templateId == patientTemplate.id) ? 1 : 2,
            isNewPatient: nikoPatientDetail.isNew
        });
    }

    @action.bound
    async deleteTemplate(patientTemplate) {
        patientTemplate.disabled = true;
        try {
            await this.patientTemplateService.deleteNewTemplate(patientTemplate.id);
            await this.fetchPatientTemplateByPatient(patientTemplate.nikoPatientDetailId);
        }
        catch (err) {
            this.rootStore.notificationStore.error("Error occurred while trying to delete a template.");
        }
        finally {
            patientTemplate.disabled = false;
        }
    }

    // --- Item Operations ---

    @action.bound
    async addEmptyItem(patientTemplate) {
        if (patientTemplate.patientTemplateItems.some(i => !i.productId)) {
            return;
        }

        await this.setProductStore(patientTemplate);
        patientTemplate.patientTemplateItems.push({
            id: uuid(),
            quantity: 0,
            salesOrderQuantity: null,
            note: undefined,
            itemCals: undefined,
            itemMls: undefined,
            mlph: undefined,
            frequency: undefined,
            procedureCode: null,
            patientTemplatId: patientTemplate.id,
            productId: null,
            product: null,
            actionId: this.insertImportActionId,
            toggle: !this.templateChangeConfirmModal.isOpen
        });
    }

    @action.bound
    async setProductStore(patientTemplate) {
        const nikoPatientDetail = this.nikoPatientDetails.find(x => x.id == patientTemplate.nikoPatientDetailId);
        const isMasterPatient = nikoPatientDetail && this.isMasterPatient(nikoPatientDetail);

        patientTemplate.productStore = new BaasicDropdownStore({
            filterable: true,
            textField: "itemId",
        }, {
            fetchFunc: async (term) => {
                if ((term && term.trim()) === '')
                    term = undefined;

                let params = {
                    searchQuery: term,
                    rpp: 50,
                    subCategoryId: patientTemplate.subCategoryId,
                    isActive: true,
                    embed: "Product,Product.ItemType,Product.ItemCategory,Product.SubCategory,Product.UnitOfMeasure,Product.ProcedureCode_"
                }

                if (this.ostomySubCategoryId == patientTemplate.subCategoryId) {
                    params = { ...params, sort: "isPreferred|desc" };
                }

                const response = await this.itemSubcategoryService.find(params);
                let filteredProducts = isMasterPatient ? response.data.item : response.data.item.filter(x => !x.product.itemId.toLowerCase().includes("-cs"));
                return _.map(filteredProducts, item => {
                    return {
                        ...item.product
                    };
                });
            }
        });
    }

    @action.bound
    async saveItemOnProductSelect(patientTemplate, patientTemplateItem, product) {
        patientTemplate.disabled = true;
        if (!patientTemplate.isNew && patientTemplate.subCategory.abrv === 'Trach-Tube' && patientTemplate.patientTemplateItems.some(x => x.productId && x.actionId !== this.deleteImportActionId)) {
            this.rootStore.modalStore.showConfirm('FACILITY_ORDER_TEMPLATE.EDIT.CONFIRM_DELTE_TRACH_TUBE_ITEMS', async () => {
                await this.saveItem(patientTemplate, patientTemplateItem, product);
            });
        }
        else {
            await this.saveItem(patientTemplate, patientTemplateItem, product);
        }
    }

    @action.bound
    async saveItem(patientTemplate, patientTemplateItem, product) {
        try {
            const nikoPatientDetail = this.nikoPatientDetails.find(x => x.id == patientTemplate.nikoPatientDetailId);
            const isMasterPatient = nikoPatientDetail && this.isMasterPatient(nikoPatientDetail);

            const isEnteral = patientTemplate.itemCategoryId === this.enteralItemCategoryId;
            const isFormula = product.itemTypeId || (product.procedureCode_ && product.procedureCode_.abrv === "B4155");

            if (isMasterPatient) {
                const response = await this.patientTemplateItemService.create({
                    product: product,
                    productId: product.id,
                    patientTemplateId: patientTemplate.id,
                    quantity: 0,
                    salesOrderQuantity: 0,
                    note: null,
                    procedureCode: product.procedureCode_ && product.procedureCode_.abrv,
                });
                const created = { ...response.data, product: product };
                const pos = patientTemplate.patientTemplateItems.findIndex((i) => patientTemplateItem.id === i.id);
                if (pos > -1) {
                    patientTemplate.patientTemplateItems.splice(pos, 1, created);
                    await this.fetchPatientTemplateByPatient(patientTemplate.nikoPatientDetailId);
                }
                else {
                    patientTemplate.patientTemplateItems.push(created);
                }
                return;
            }

            let tempItem = {
                product: product,
                productId: product.id,
                procedureCode: product.procedureCode_ && product.procedureCode_.abrv,
            };

            if (isEnteral && isFormula) {
                _.extend(tempItem, { itemMls: 1, itemCals: product.calsPerMl });
            }

            const items = (await this.patientTemplateItemService.createAdditionalItems(
                {
                    patientTemplateItem: tempItem,
                    patientTemplateItems: patientTemplate.patientTemplateItems.filter((i) => i.productId).map((i) => { return { id: i.id, product: i.product, productId: i.productId, procedureCode: i.procedureCode, actionId: i.actionId }; }),
                    facilityId: this.facilityOrder.facilityId,
                    subCategoryId: patientTemplate.subCategoryId,
                    clinicalStatusId: patientTemplate.clinicalStatusId,
                    patientTemplateId: patientTemplate.id,
                    pageName: this.pageName,
                    tabName: this.selectedStatusAbrv,
                }
            )).data;

            for (const item of items) {
                const existingItem = patientTemplate.patientTemplateItems.find(x => x.id === item.id);
                if (existingItem) {
                    existingItem.procedureCode = item.procedureCode;
                    continue;
                }

                const response = await this.patientTemplateItemService.create({
                    id: item.id,
                    product: item.product,
                    productId: item.product.id,
                    patientTemplateId: patientTemplate.id,
                    quantity: item.quantity || 0,
                    salesOrderQuantity: item.salesOrderQuantity || 0,
                    note: item.note || null,
                    procedureCode: item.procedureCode,
                });
                const created = { ...response.data, product: item.product };
                const pos = patientTemplate.patientTemplateItems.findIndex((i) => patientTemplateItem.id === i.id);
                if (pos > -1) {
                    patientTemplate.patientTemplateItems.splice(pos, 1, created);
                    await this.fetchPatientTemplateByPatient(patientTemplate.nikoPatientDetailId);
                }
                else {
                    patientTemplate.patientTemplateItems.push(created);
                }
            }

            if (isEnteral && isFormula) {
                const formulaItem = patientTemplate.patientTemplateItems.find(i => i.product && i.product.id === tempItem.productId);
                if (formulaItem) {
                    this.openNikoPatientTemplateItemCalculationModal(formulaItem, patientTemplate);
                }
            }
        }
        catch (err) {
            this.rootStore.notificationStore.error(err.data ? err.data : "Error while adding additional items");
        }
        finally {
            patientTemplate.disabled = false;
        }
    }

    @action.bound
    openNikoPatientTemplateItemCalculationModal(patientTemplateItem, patientTemplate) {
        this.nikoPatientTemplateItemCalculationModal.open({
            patientTemplateItem: patientTemplateItem,
            facilityId: this.facilityOrder.facilityId,
            patientTemplateItemService: this.patientTemplateItemService,
            updateItem: async (patientTemplateItem) => {
                await this.updateItem(patientTemplateItem);
                await this.fetchPatientTemplateItemsById(patientTemplate.nikoPatientDetailId, patientTemplate.id);
                await this.fetchPatientTemplateIntakeStatusById(patientTemplate);
            },
        });
    }

    @action.bound
    showItemQuantities(patientTemplate, show) {
        this.templateExpanded = { id: patientTemplate.id, expanded: show };
    }

    @action.bound
    editItem(item, propertyName, value) {
        const initialItem = item.initialPatientTemplateItem;
        const fieldMap = { quantity: 'Quantity', salesOrderQuantity: 'SalesOrderQuantity', note: 'Note' };
        const confirmedMap = { quantity: 'isQuantityConfirmed', salesOrderQuantity: 'isSalesOrderQuantityConfirmed', note: 'isNoteConfirmed' };

        if (initialItem && fieldMap[propertyName] && initialItem[fieldMap[propertyName]] == item[propertyName]) {
            set(item, confirmedMap[propertyName], false);
        }

        if (propertyName === "quantity" && initialItem && initialItem.SalesOrderQuantity == item.salesOrderQuantity) {
            set(item, 'isSalesOrderQuantityConfirmed', false);
        }

        item[propertyName] = value;
        if (propertyName === "quantity") {
            item["salesOrderQuantity"] = value;
        }
    }

    @action.bound
    async updateItem(patientTemplateItem, patientTemplate) {
        if (patientTemplate) {
            patientTemplate.disabled = true;
        }

        if (Number.isNaN(patientTemplateItem.quantity) || patientTemplateItem.quantity === '' || patientTemplateItem.quantity === null || patientTemplateItem.quantity === undefined) {
            patientTemplateItem.quantity = 0;
        }
        if (Number.isNaN(patientTemplateItem.salesOrderQuantity) || patientTemplateItem.salesOrderQuantity === '' || patientTemplateItem.salesOrderQuantity === null || patientTemplateItem.salesOrderQuantity === undefined) {
            patientTemplateItem.salesOrderQuantity = 0;
        }

        if (patientTemplateItem.quantity == patientTemplateItem.savedQuantity &&
            patientTemplateItem.salesOrderQuantity == patientTemplateItem.savedSalesOrderQuantity &&
            patientTemplateItem.note == patientTemplateItem.savedNote) {
            if (patientTemplate) {
                patientTemplate.disabled = false;
            }
            return;
        }

        await this.patientTemplateItemService.update(patientTemplateItem.id, {
            productId: patientTemplateItem.productId,
            patientTemplateId: patientTemplateItem.patientTemplateId,
            quantity: patientTemplateItem.quantity,
            salesOrderQuantity: patientTemplateItem.salesOrderQuantity,
            note: patientTemplateItem.note,
            procedureCode: patientTemplateItem.procedureCode,
            mlph: patientTemplateItem.mlph,
            itemMls: patientTemplateItem.itemMls,
            itemCals: patientTemplateItem.itemCals,
            frequency: patientTemplateItem.frequency,
            actionId: patientTemplateItem.actionId
        });

        patientTemplateItem.savedQuantity = patientTemplateItem.quantity;
        patientTemplateItem.savedSalesOrderQuantity = patientTemplateItem.salesOrderQuantity;
        patientTemplateItem.savedNote = patientTemplateItem.note;
        set(patientTemplateItem, 'isQuantityConfirmed', true);
        set(patientTemplateItem, 'isSalesOrderQuantityConfirmed', true);
        set(patientTemplateItem, 'isNoteConfirmed', true);
        if (patientTemplate) {
            patientTemplate.isDifferentQty = patientTemplate.patientTemplateItems.some(x => x.quantity != x.salesOrderQuantity);
            await this.fetchPatientTemplateIntakeStatusById(patientTemplate);
            patientTemplate.disabled = false;
        }
    }

    @action.bound
    async deletePatientTemplateItem(patientTemplateItem, patientTemplate, nikoPatientDetail) {
        patientTemplate.disabled = true;
        if (nikoPatientDetail.isNew && nikoPatientDetail.patientTemplates.every(x => x.id == patientTemplate.id || x.templateId == patientTemplate.id) && patientTemplate.patientTemplateItems.length == 1) {
            const params = { facilityOrderId: this.id, nikoPatientDetailId: patientTemplate.nikoPatientDetailId, pageSize: 3, fields: 'Id,TemplateId' };
            const response = (await this.patientTemplateService.find(params)).data.item;

            if (response.every(x => x.id == patientTemplate.id || x.templateId == patientTemplate.id)) {
                return this.nikoPatientTemplateItemNewFlowDeleteQuestionModal.open({
                    deleteItem: () => this.deleteItem(patientTemplate, patientTemplateItem, false),
                    deletePatient: () => this.deleteItem(patientTemplate, patientTemplateItem, true)
                });
            }
        }

        if (patientTemplateItem.actionId == this.insertImportActionId) {
            await this.deleteItem(patientTemplate, patientTemplateItem, patientTemplate.isNew);
        }
        else {
            await this.setAction(patientTemplateItem, this.deleteImportActionId, patientTemplate);
        }
        patientTemplate.disabled = false;
    }

    @action.bound
    async deleteItem(patientTemplate, patientTemplateItem, deleteTemplate) {
        patientTemplate.disabled = true;
        if (deleteTemplate && patientTemplate.patientTemplateItems.length === 1) {
            return await this.deleteTemplate(patientTemplate);
        }

        if (patientTemplateItem.productId) {
            const procedureCode = patientTemplateItem.product && patientTemplateItem.product.procedureCode_ && patientTemplateItem.product.procedureCode_.abrv;
            const hasSyringeItem = patientTemplate.patientTemplateItems && patientTemplate.patientTemplateItems.some(x => x.product && x.product.defaultItemTypeId == this.syringeDefaultItemTypeId);
            await this.patientTemplateItemService.delete(patientTemplateItem.id, { setProcedureCode: (procedureCode == 'B4035' || procedureCode == 'B4036') && hasSyringeItem });
            runInAction(() => {
                patientTemplate.patientTemplateItems = patientTemplate.patientTemplateItems.filter((i) => i !== patientTemplateItem);
            });

            if ((procedureCode == 'B4035' || procedureCode == 'B4036') && hasSyringeItem) {
                await this.fetchPatientTemplateItemsById(patientTemplate.nikoPatientDetailId, patientTemplate.id);
            }
            patientTemplate.isDifferentQty = patientTemplate.patientTemplateItems.some(x => x.quantity != x.salesOrderQuantity);
            await this.fetchPatientTemplateIntakeStatusById(patientTemplate);
        }
        else {
            runInAction(() => {
                patientTemplate.patientTemplateItems = patientTemplate.patientTemplateItems.filter((i) => i !== patientTemplateItem);
            });
            patientTemplate.isDifferentQty = patientTemplate.patientTemplateItems.some(x => x.quantity != x.salesOrderQuantity);
        }

        patientTemplate.disabled = false;
    }

    @action.bound
    async swapPatientTemplateItem(patientTemplateItem, patientTemplate, nikoPatientDetail) {
        patientTemplate.disabled = true;
        try {
            if (patientTemplateItem.actionId === this.deleteImportActionId) {
                await this.setAction(patientTemplateItem, this.updateImportActionId, patientTemplate)
            }
            else {
                await this.deletePatientTemplateItem(patientTemplateItem, patientTemplate, nikoPatientDetail)
            }
            this.addEmptyItem(patientTemplate);
        }
        catch (err) {
            this.rootStore.notificationStore.error("FACILITY_ORDER_TEMPLATE_ITEM.EDIT.DELETE_ERROR");
        }
        patientTemplate.disabled = false;
    }

    @action.bound
    async setAction(patientTemplateItem, actionId, patientTemplate) {
        patientTemplateItem.actionId = actionId;
        const procedureCode = patientTemplateItem.product && patientTemplateItem.product.procedureCode_ && patientTemplateItem.product.procedureCode_.abrv;
        const hasSyringeItem = patientTemplate.patientTemplateItems && patientTemplate.patientTemplateItems.some(x => x.product && x.product.defaultItemTypeId == this.syringeDefaultItemTypeId);
        await this.patientTemplateItemService.update(patientTemplateItem.id, { ...patientTemplateItem, setProcedureCode: (procedureCode == 'B4035' || procedureCode == 'B4036') && hasSyringeItem });
        if ((procedureCode == 'B4035' || procedureCode == 'B4036') && hasSyringeItem) {
            await this.fetchPatientTemplateItemsById(patientTemplate.nikoPatientDetailId, patientTemplate.id);
        }
        await this.fetchPatientTemplateIntakeStatusById(patientTemplate);
    }

    // --- History ---

    @action.bound
    async getFieldHistory(item, fieldName, value) {
        if (item.history) {
            let history;
            if (fieldName === 'ImportAction') {
                history = (await this.historyService.getItemActionHistory(item.id)).data;
            } else {
                history = (await this.historyService.getRecordHistory({ recordId: item.id, fieldName: fieldName, value: value })).data;
            }
            item.history.userName = history.userName;
            item.history.eventDate = history.eventDate;
            item.history.fieldName = fieldName;
            if (history.action) item.history.action = history.action;
        }
    }

    getChangedFields(nikoPatientDetail) {
        var fields = [];
        var npd = nikoPatientDetail;
        if (!npd.isNew) {
            if (npd.primaryActive.insuranceId.current != npd.primaryActive.insuranceId.initial)
                fields.push({ fieldName: "PrimaryActiveInsuranceId", value: npd.primaryActive.insuranceId.current || null, initialValue: npd.primaryActive.insuranceId.initial || null });
            if (npd.primaryActive.insuranceId.current == npd.primaryActive.insuranceId.initial && npd.primaryActive.policyNo.current != npd.primaryActive.policyNo.initial)
                fields.push({ fieldName: "PrimaryActivePolicyNo", value: npd.primaryActive.policyNo.current || null, initialValue: npd.primaryActive.policyNo.initial || null });
            if (npd.primaryInactive.insuranceId.current && npd.primaryInactive.insuranceId.current != npd.primaryInactive.insuranceId.initial)
                fields.push({ fieldName: "PrimaryInactiveInsuranceId", value: npd.primaryInactive.insuranceId.current || null, initialValue: npd.primaryInactive.insuranceId.initial || null });
            if (npd.primaryInactive.insuranceId.current && npd.primaryInactive.insuranceId.current == npd.primaryInactive.insuranceId.initial && npd.primaryInactive.policyNo.current != npd.primaryInactive.policyNo.initial)
                fields.push({ fieldName: "PrimaryInactivePolicyNo", value: npd.primaryInactive.policyNo.current || null, initialValue: npd.primaryInactive.policyNo.initial || null });
            if (npd.secondaryActive.insuranceId.current != npd.secondaryActive.insuranceId.initial)
                fields.push({ fieldName: "SecondaryActiveInsuranceId", value: npd.secondaryActive.insuranceId.current || null, initialValue: npd.secondaryActive.insuranceId.initial || null });
            if (npd.secondaryActive.insuranceId.current == npd.secondaryActive.insuranceId.initial && npd.secondaryActive.policyNo.current != npd.secondaryActive.policyNo.initial)
                fields.push({ fieldName: "SecondaryActivePolicyNo", value: npd.secondaryActive.policyNo.current || null, initialValue: npd.secondaryActive.policyNo.initial || null });
            if (npd.secondaryInactive.insuranceId.current && npd.secondaryInactive.insuranceId.current != npd.secondaryInactive.insuranceId.initial)
                fields.push({ fieldName: "SecondaryInactiveInsuranceId", value: npd.secondaryInactive.insuranceId.current || null, initialValue: npd.secondaryInactive.insuranceId.initial || null });
            if (npd.secondaryInactive.insuranceId.current && npd.secondaryInactive.insuranceId.current == npd.secondaryInactive.insuranceId.initial && npd.secondaryInactive.policyNo.current != npd.secondaryInactive.policyNo.initial)
                fields.push({ fieldName: "SecondaryInactivePolicyNo", value: npd.secondaryInactive.policyNo.current || null, initialValue: npd.secondaryInactive.policyNo.initial || null });
            if (npd.eligibilityStatusId != (npd.initialNikoPatientDetail ? npd.initialNikoPatientDetail.EligibilityStatusId : null))
                fields.push({ fieldName: "EligibilityStatusId", value: npd.eligibilityStatusId || null, initialValue: npd.initialNikoPatientDetail ? npd.initialNikoPatientDetail.EligibilityStatusId || null : null });
            npd.patientTemplates.forEach(function (pt) {
                if (!pt.isNew && (pt.isPriorAuthNeeded ? true : false) != (pt.initialPatientTemplate && pt.initialPatientTemplate.IsPriorAuthNeeded ? true : false))
                    fields.push({ fieldName: "PAR_" + pt.id, value: pt.isPriorAuthNeeded ? "TRUE" : "FALSE", initialValue: pt.initialPatientTemplate && pt.initialPatientTemplate.IsPriorAuthNeeded ? "TRUE" : "FALSE" });
            });
        }
        if (npd.note)
            fields.push({ fieldName: "Note", value: npd.note, initialValue: null });
        return fields;
    }

    @action.bound
    setShowInsuranceChangeHistory(value) {
        this.showInsuranceChangeHistory = value;
    }

    @action.bound
    async fetchInsuranceChangeHistory(nikoPatientDetail) {
        var fields = this.getChangedFields(nikoPatientDetail);
        if (fields.length === 0) return;
        try {
            const response = await this.historyService.getInsuranceChangeHistory(nikoPatientDetail.id, fields);
            const data = response.data || response;
            var historyMap = {};
            if (Array.isArray(data)) {
                data.forEach(function (entry) {
                    historyMap[entry.fieldName] = entry;
                });
            }
            runInAction(() => {
                this.insuranceChangeHistoryMap = Object.assign({}, this.insuranceChangeHistoryMap, { [nikoPatientDetail.id]: historyMap });
            });
        } catch (err) {
            runInAction(() => {
                this.insuranceChangeHistoryMap = Object.assign({}, this.insuranceChangeHistoryMap, { [nikoPatientDetail.id]: {} });
            });
        }
    }

    // --- Clinical Inline Stores ---

    @action.bound
    async initializeClinicalStores(patientTemplate, nikoPatientDetail) {
        if (patientTemplate.clinicalStoresInitialized) return;

        const isMaster = this.isMasterPatient(nikoPatientDetail);

        patientTemplate.clinicalStatusStore = new BaasicDropdownStore({}, {
            fetchFunc: async () => isMaster ? this.masterPatientClinicalStatuses : this.clinicalStatuses,
            onChange: async (value) => {
                patientTemplate.clinicalStatusId = value;
                await this.updateClinicalField(patientTemplate, nikoPatientDetail);
            }
        });
        if (!patientTemplate.clinicalStatusId) {
            patientTemplate.clinicalStatusStore.setValue({ id: null, name: "Select" });
        }

        patientTemplate.missingDocumentStore = new BaasicDropdownStore({ multi: true, filterable: true }, {
            fetchFunc: (term) => {
                let items = this.missingDocuments.filter(i => i.itemCategoryId === patientTemplate.itemCategoryId || !i.itemCategoryId);
                if (term && term.trim()) {
                    const lowerTerm = term.toLowerCase();
                    items = items.filter(i => i.name && i.name.toLowerCase().includes(lowerTerm));
                }
                return this.filterMissingDocumentStore(items, patientTemplate);
            }
        });

        // Map missing documents for dropdown display
        if (patientTemplate.patientTemplateMissingDocuments) {
            patientTemplate.mappedMissingDocuments = patientTemplate.patientTemplateMissingDocuments.map(i => ({
                ...i,
                name: i.missingDocument.name,
                note: i.note,
                isPreviousDos: i.isPreviousDos,
                patientTemplateId: patientTemplate.id
            }));
        } else {
            patientTemplate.mappedMissingDocuments = [];
        }

        patientTemplate.clinicalStoresInitialized = true;
    }

    @action.bound
    async initializeThirdRowStores(patientTemplate, nikoPatientDetail) {
        if (patientTemplate.thirdRowStoresInitialized) return;

        // Fetch fresh clinical data
        let params = {
            embed: 'NikoPatientDetail,OrderingDoctor,PatientTemplateDiagnosisCodes',
            fields: 'nikoPatientDetail,orderingDoctor,orderingDoctorId,patientTemplateDiagnosisCodes',
        };
        const template = (await this.patientTemplateService.get(patientTemplate.id, params)).data;
        patientTemplate.orderingDoctor = template.orderingDoctor;
        patientTemplate.orderingDoctorId = template.orderingDoctorId;
        patientTemplate.patientTemplateDiagnosisCodes = template.patientTemplateDiagnosisCodes;
        if (template.nikoPatientDetail) {
            nikoPatientDetail.height = template.nikoPatientDetail.height;
            nikoPatientDetail.weight = template.nikoPatientDetail.weight;
        }

        // Create DX code stores
        for (let i = 1; i <= 8; i++) {
            patientTemplate[`dxCode${i}Store`] = this.createDxCodeDropdownStore(patientTemplate, i);
        }

        // Create doctor store
        patientTemplate.doctorStore = new BaasicDropdownStore({
            textField: 'orderingDoctor',
            filterable: true,
            returnObject: true,
            initFetch: false,
        }, {
            fetchFunc: async (term) => {
                const response = await this.localDoctorService.find({ searchQuery: term, rpp: 20, isNiko: true });
                return response.data.item.map(item => ({
                    ...item,
                    orderingDoctor: item.name + " - " + item.npi + (item.primaryAddressState ? " - " + item.primaryAddressState : ""),
                }));
            }
        });

        // Initialize doctor store value
        await patientTemplate.doctorStore.filterAsync(patientTemplate.orderingDoctor && patientTemplate.orderingDoctor.npi);

        // Set DX code store values
        if (patientTemplate.patientTemplateDiagnosisCodes) {
            patientTemplate.patientTemplateDiagnosisCodes.forEach(ptdc => {
                const store = patientTemplate[`dxCode${ptdc.order}Store`];
                if (store) {
                    store.setValue(ptdc.diagnosisCode);
                }
            });
        }

        patientTemplate.thirdRowStoresInitialized = true;
    }

    createDxCodeDropdownStore(patientTemplate) {
        return new NikoDxCodeDropdownStore(
            { filterable: true, dataItemKey: "id", textField: "code", returnObject: true },
            {
                fetchFunc: async (term) => {
                    if (!term || term.trim() === '') {
                        term = undefined;
                    }
                    const response = await this.diagnosisCodeService.find({ searchQuery: term, rpp: 10, itemCategoryId: patientTemplate.itemCategoryId, isBillable: true });
                    return response.data.item;
                }
            }
        );
    }

    filterMissingDocumentStore(missingDocuments, patientTemplate) {
        let result = missingDocuments;
        if (missingDocuments && missingDocuments.some(m => m)) {
            let patientTemplateItems = patientTemplate && patientTemplate.patientTemplateItems;
            const deleteActionId = this.deleteImportActionId;

            const specialtyTypes = {
                'diabetic-formula': 'specialty-product-justification-diabetic',
                'renal-formula-nepro-nr': 'specialty-product-justification-renal-nepro',
                'renal-formula-suplena': 'specialty-product-justification-renal-suplena',
                'pulmonary-formula': 'specialty-product-justification-pulmonary',
                'elemental-formula': 'specialty-product-justification-elemental',
                'protein-supplement': 'specialty-product-justification-protein-supplement',
                'silicone-catheter': 'specialty-product-justification-silicone-catheter',
                'coude-catheter': 'specialty-product-justification-coude-catheter',
            };

            Object.entries(specialtyTypes).forEach(([specialtyAbrv, missingDocAbrv]) => {
                const specialtyTypeId = this.lookups.specialtyType.find(x => x.abrv === specialtyAbrv);
                if (specialtyTypeId && !(patientTemplateItems && patientTemplateItems.some(i => i.actionId != deleteActionId && i.product && i.product.specialtyTypeId == specialtyTypeId.id))) {
                    result = result.filter(i => i.abrv !== missingDocAbrv);
                }
            });
        }
        return result;
    }

    // --- Clinical Field Updates ---

    @action.bound
    async updateClinicalField(patientTemplate, nikoPatientDetail) {
        try {
            patientTemplate.disabled = true;

            const zeroToNull = value => value === 0 ? null : value;
            const dxCodes = [];
            for (let i = 1; i <= 8; i++) {
                const store = patientTemplate[`dxCode${i}Store`];
                if (store && store.value && store.value.id && store.value.id.trim() !== "") {
                    dxCodes.push({ diagnosisCodeId: store.value.id, order: i });
                }
            }
            const missingDocs = patientTemplate.mappedMissingDocuments
                ? patientTemplate.mappedMissingDocuments.map(i => ({ id: i.id, missingDocumentId: i.missingDocumentId, note: i.note, isPreviousDos: i.isPreviousDos }))
                : [];

            const resource = {
                id: patientTemplate.id,
                clinicalStatusId: patientTemplate.clinicalStatusId,
                salesOrderNote: patientTemplate.tempSalesOrderNote,
                expirationDate: patientTemplate.tempExpirationDate,
                orderingDoctorId: patientTemplate.doctorStore && patientTemplate.doctorStore.value ? patientTemplate.doctorStore.value.id : patientTemplate.orderingDoctorId,
                height: nikoPatientDetail ? zeroToNull(nikoPatientDetail.height) : undefined,
                weight: nikoPatientDetail ? zeroToNull(nikoPatientDetail.weight) : undefined,
                patientTemplateMissingDocuments: missingDocs,
                patientTemplateDiagnosisCodes: dxCodes,
            };

            await this.patientTemplateService.updateClinicalInformation(resource);
            await this.fetchPatientTemplateByPatient(patientTemplate.nikoPatientDetailId);
        } catch (err) {
            this.rootStore.notificationStore.error(getErrorMessage(err, "Error updating clinical information."));
        } finally {
            patientTemplate.disabled = false;
        }
    }

    @action.bound
    async updateSalesOrderNote(patientTemplate, nikoPatientDetail) {
        if (patientTemplate.tempSalesOrderNote !== patientTemplate.salesOrderNote) {
            patientTemplate.salesOrderNote = patientTemplate.tempSalesOrderNote;
            await this.updateClinicalField(patientTemplate, nikoPatientDetail);
        }
    }

    @action.bound
    async updateExpirationDate(patientTemplate, nikoPatientDetail) {
        if (patientTemplate.tempExpirationDate !== patientTemplate.expirationDate) {
            patientTemplate.expirationDate = patientTemplate.tempExpirationDate;
            await this.updateClinicalField(patientTemplate, nikoPatientDetail);
        }
    }

    @action.bound
    async updateMissingDocuments(patientTemplate, nikoPatientDetail, value) {
        const values = value.map(item => {
            if (item.abrv) {
                return {
                    id: undefined,
                    name: item.name,
                    missingDocumentId: item.id,
                    missingDocument: item,
                    note: item.note,
                    isPreviousDos: false,
                    patientTemplateId: patientTemplate.id
                };
            }
            return item;
        });
        patientTemplate.mappedMissingDocuments = values;
        await this.updateClinicalField(patientTemplate, nikoPatientDetail);
    }

    @action.bound
    async updatePatientHeight(nikoPatientDetail) {
        try {
            nikoPatientDetail.disabled = true;
            await this.nikoPatientDetailService.updatePatientInformation({ id: nikoPatientDetail.id, height: nikoPatientDetail.height, weight: nikoPatientDetail.weight });
        } catch (err) {
            this.rootStore.notificationStore.error(getErrorMessage(err, "Error updating patient information."));
        } finally {
            nikoPatientDetail.disabled = false;
        }
    }

    @action.bound
    async updatePatientWeight(nikoPatientDetail) {
        await this.updatePatientHeight(nikoPatientDetail);
    }

    // --- Document Generation ---

    @action.bound
    async generateStandardWrittenOrder(patientTemplate, nikoPatientDetail) {
        try {
            if (patientTemplate.thirdRowStoresInitialized) {
                await this.updateClinicalField(patientTemplate, nikoPatientDetail);
            }
            const url = `${this.patientTemplateService.getStandardWrittenOrderReportUrl(patientTemplate.id)}?contentDisposition=attachment&token=${this.rootStore.authStore.token.tokenUrl}&ttype=${this.rootStore.authStore.token.type}`;
            window.open(url, "_blank");
            this.rootStore.notificationStore.success("Standard Written Order successfully generated.");
        } catch (err) {
            this.rootStore.notificationStore.error("Error occurred while trying to generate Standard Written Order.");
        }
    }

    @action.bound
    async generateMedicalNecessityForm(patientTemplate, nikoPatientDetail) {
        try {
            if (patientTemplate.thirdRowStoresInitialized) {
                await this.updateClinicalField(patientTemplate, nikoPatientDetail);
            }

            if (patientTemplate.itemCategory && patientTemplate.itemCategory.abrv === "enteral") {
                const missingInfo = this.getMissingInformationText(patientTemplate, nikoPatientDetail);
                if (missingInfo) {
                    this.templatePNMissingInformationModal.open({
                        missingInformation: missingInfo,
                        generateMedicalNecessityForm: () => this.openMedicalNecessityWindow(patientTemplate)
                    });
                    return;
                }
            }

            if (patientTemplate.itemCategory && patientTemplate.itemCategory.abrv === "urological") {
                if (this.getMissingInformationUrological(patientTemplate)) {
                    this.templatePNMissingInformationModal.open({
                        missingInformation: ["Qty per day (system cannot determine)"],
                        generateMedicalNecessityForm: () => this.openMedicalNecessityWindow(patientTemplate)
                    });
                    return;
                }
            }

            this.openMedicalNecessityWindow(patientTemplate);
        } catch (err) {
            this.rootStore.notificationStore.error("Error occurred while trying to generate Medical Necessity Form.");
        }
    }

    @action.bound
    async generateMedicalNecessityFormAndStandardWrittenOrder(patientTemplate, nikoPatientDetail) {
        await this.generateMedicalNecessityForm(patientTemplate, nikoPatientDetail);
        await this.generateStandardWrittenOrder(patientTemplate, nikoPatientDetail);
    }

    @action.bound
    openMedicalNecessityWindow(patientTemplate) {
        const url = `${this.patientTemplateService.getMedicalNecessityFormReportUrl(patientTemplate.id)}?contentDisposition=attachment&token=${this.rootStore.authStore.token.tokenUrl}&ttype=${this.rootStore.authStore.token.type}`;
        window.open(url, "_blank");
        this.rootStore.notificationStore.success("Progress Notes document successfully generated.");
    }

    getMissingInformationText(patientTemplate, nikoPatientDetail) {
        let missingItems = [];
        if (nikoPatientDetail && !nikoPatientDetail.weight) missingItems.push("weight");
        if (nikoPatientDetail && !nikoPatientDetail.height) missingItems.push("height");
        if (patientTemplate.patientTemplateItems.some(x => x.product && x.product.itemTypeId && !x.itemCals)) missingItems.push("total calories");
        if (nikoPatientDetail && nikoPatientDetail.nikoPatient && !nikoPatientDetail.nikoPatient.gender) missingItems.push("gender");
        if (patientTemplate.patientTemplateItems.some(x => x.product && x.product.itemTypeId && !x.mlph)) missingItems.push("flow rate");
        missingItems.sort();
        return missingItems.length > 0 ? missingItems : null;
    }

    getMissingInformationUrological(patientTemplate) {
        return patientTemplate.patientTemplateItems.some(x =>
            x.product && x.product.procedureCode_ &&
            (x.product.procedureCode_.abrv === "A4352" || x.product.procedureCode_.abrv === "A4353" || x.product.procedureCode_.abrv === "A4351") &&
            (!x.quantity || x.quantity == 0 || x.quantity % 28 != 0)
        );
    }

    // --- Sync & Toggle ---

    @action.bound
    async syncOrderingDoctors() {
        if (this.isSyncInProgress) return;
        this.isSyncInProgress = true;
        try {
            await this.localDoctorService.nikoSync();
            this.rootStore.notificationStore.success("Sync Niko Ordering Doctors successfully done.");
        } catch (err) {
            this.rootStore.notificationStore.error(err && err.data && err.data.errors ? err.data.errors : 'Error occurred while trying to sync niko ordering doctors.');
        }
        this.isSyncInProgress = false;
    }

    @action.bound
    async toggleThirdRow(patientTemplate, nikoPatientDetail) {
        if (!patientTemplate.thirdRowStoresInitialized) {
            await this.initializeThirdRowStores(patientTemplate, nikoPatientDetail);
        }
        patientTemplate.showThirdRow = !patientTemplate.showThirdRow;
    }

    // --- Clinical Modals ---

    @action.bound
    openClinicalStatusNoteModal(patientTemplate) {
        this.patientTemplateClinicalStatusNoteModal.open({
            patientTemplate: patientTemplate
        });
    }

    @action.bound
    openMissingDocumentNoteModal(patientTemplate, patientTemplateMissingDocument) {
        this.nikoTemplateMissingDocumentNoteModal.open({
            patientTemplateMissingDocument: patientTemplateMissingDocument,
            updateMissingDocNote: (targetId, newNote, isPreviousDos) => this.updateMissingDocNote(patientTemplate, targetId, newNote, isPreviousDos)
        });
    }

    @action.bound
    async updateMissingDocNote(patientTemplate, targetId, newNote, isPreviousDos) {
        patientTemplate.mappedMissingDocuments = patientTemplate.mappedMissingDocuments.map(doc => doc.id === targetId ? { ...doc, note: newNote, isPreviousDos: isPreviousDos } : doc);
    }

    getMissingDocsPreviousValue(patientTemplate) {
        if (!patientTemplate.initialPatientTemplate || !patientTemplate.initialPatientTemplate.PatientTemplateMissingDocuments || !patientTemplate.initialPatientTemplate.PatientTemplateMissingDocuments.some(x => x)) {
            return (!patientTemplate.patientTemplateMissingDocuments || !patientTemplate.patientTemplateMissingDocuments.some(x => x)) ? null : "";
        }
        let current = _.sortBy(patientTemplate.patientTemplateMissingDocuments, md => md.missingDocument.name);
        let initial = _.sortBy(patientTemplate.initialPatientTemplate.PatientTemplateMissingDocuments, mdi => mdi.MissingDocument.Name);
        if (current.map(mdi => mdi.missingDocument.name).join(", ") != initial.map(mdi => mdi.MissingDocument.Name).join(", ")) {
            return initial.map(mdi => mdi.MissingDocument.Name).join(", ");
        }
        return null;
    }

    isEligible(nikoPatientDetail) {
        return nikoPatientDetail && nikoPatientDetail.eligibilityStatus && nikoPatientDetail.eligibilityStatus.name === "Eligible";
    }

    @action.bound
    openPatientTemplateNoteModal(patientTemplate) {
        this.patientTemplateNoteModal.open({
            patientTemplate: patientTemplate
        });
    }

    @action.bound
    hasMeaningfulOrderChange(patientTemplate) {
        if (!patientTemplate || patientTemplate.isNew || !patientTemplate.patientTemplateItems) {
            return false;
        }
        return patientTemplate.patientTemplateItems.some(item =>
            (item.actionId === this.insertImportActionId && item.productId) ||
            (item.initialPatientTemplateItem && item.initialPatientTemplateItem.ItemCals != item.itemCals)
        );
    }

    @action.bound
    goToNikoHealthSalesOrder(salesOrderId) {
        window.open(`${this.nikoHealthBaseUrl}v2/#/orders/order/${salesOrderId}/details`);
    }

    @action.bound
    goToNikoHealthInvoice(invoiceId) {
        window.open(`${this.nikoHealthBaseUrl}v2/#/billing/invoices/invoice/${invoiceId}/details`);
    }

    // ISG-2663: attach Niko invoice ids ({ remoteId, displayId }, oldest-first) to each template for the header.
    async attachNikoInvoiceIds(templates) {
        const ids = templates.map(t => t.id).filter(Boolean);
        if (!ids.length) return;
        const invoiceMap = (await this.patientTemplateService.getNikoInvoiceIds(ids)).data || {};
        const invoicesByTemplateId = {};
        Object.keys(invoiceMap).forEach(key => { invoicesByTemplateId[key.toLowerCase()] = invoiceMap[key]; });
        templates.forEach(t => {
            t.nikoInvoices = invoicesByTemplateId[(t.id || '').toLowerCase()] || [];
        });
    }
}

export default NikoFacilityOrderClinicalReviewPreviewViewStore;
